import type { CSSProperties } from "react";
import type { GetServerSidePropsContext } from "next";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { Inter, Manrope } from "next/font/google";
import HomeFooter from "../components/homepage/HomeFooter";
import HomeHeader from "../components/homepage/HomeHeader";
import { ArrowRightIcon } from "../components/homepage/icons";
import {
  CONTAINER_CLASS,
  HOMEPAGE_SECTION_IDS,
  NEWS_SITE_URL,
} from "../components/homepage/homepageLinks";

const manrope = Manrope({
  subsets: ["latin"],
  weight: ["600", "700", "800"],
  variable: "--font-manrope",
  display: "swap",
});

const inter = Inter({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
  variable: "--font-inter",
  display: "swap",
});

const fontVariables = {
  "--font-display": "var(--font-manrope)",
  "--font-body": "var(--font-inter)",
} as CSSProperties;

const SITE_URL = "https://www.youdle.io";
const PAGE_TITLE = "About Youdle | Built around shoppers helping shoppers";
const PAGE_DESCRIPTION =
  "Youdle grew from a simple idea: people make better shopping decisions when they can share useful information with one another.";

const features = [
  {
    label: "Build a grocery list",
    description: "Keep your list in one place.",
    href: "/grocery",
    external: false,
  },
  {
    label: "Community conversation",
    description: "See what shoppers are finding and sharing.",
    href: "/community",
    external: false,
  },
  {
    label: "Grocery News",
    description: "Recalls, prices and new finds.",
    href: NEWS_SITE_URL,
    external: true,
  },
  {
    label: "Compare prices",
    description: "Compare nearby store prices by retailer.",
    href: `/#${HOMEPAGE_SECTION_IDS.search}`,
    external: false,
  },
];

export const getServerSideProps = (context: GetServerSidePropsContext) => {
  const webView = context.query.webView;
  return {
    props: {
      webView: !!webView,
    },
  };
};

const AboutUs = ({ webView }: { webView: boolean }) => {
  return (
    <>
      <Head>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <link rel="canonical" href={`${SITE_URL}/about-us`} />
        <meta name="theme-color" content="#173E2F" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Youdle" />
        <meta property="og:url" content={`${SITE_URL}/about-us`} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
      </Head>

      <div
        className={`${manrope.variable} ${inter.variable} yd-home min-h-screen bg-home-cream font-ui text-home-ink antialiased`}
        style={fontVariables}
      >
        {!webView && <HomeHeader />}

        <main id="main">
          <section className="bg-home-cream py-4 md:py-5 lg:py-4">
            <div className={CONTAINER_CLASS}>
              <div className="grid items-stretch gap-3 lg:grid-cols-[1.18fr_0.82fr] lg:gap-4">
                <div className="flex min-h-[176px] flex-col justify-center rounded-[18px] border border-home-hairline bg-white px-5 py-4 md:px-6">
                  <h1 className="font-display text-[27px] font-extrabold leading-[1.05] tracking-[-0.025em] text-home-forest md:text-[32px] lg:text-[34px]">
                    Built around shoppers helping shoppers.
                  </h1>
                  <p className="mt-2.5 max-w-[760px] font-ui text-[14px] leading-[1.48] text-home-muted md:text-[15px]">
                    Youdle grew from a simple idea: people make better shopping decisions when they can share useful information with one another.
                  </p>
                  <p className="mt-2 max-w-[760px] font-ui text-[14px] leading-[1.48] text-home-muted md:text-[15px]">
                    That same idea still drives us today. Better information can save someone time, money and an unnecessary trip.
                  </p>
                </div>

                <div className="relative min-h-[176px] overflow-hidden rounded-[18px] border border-home-hairline bg-white shadow-[0_10px_26px_rgba(23,62,47,0.08)]">
                  <Image
                    src="/img/homepage/hero-grocery-tote.webp"
                    alt="Fresh produce and everyday groceries in a shopping tote"
                    fill
                    priority
                    sizes="(max-width: 1024px) 100vw, 36vw"
                    className="object-cover"
                  />
                </div>
              </div>

              <nav aria-label="Youdle features" className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {features.map(({ label, description, href, external }) => (
                  <Link
                    key={label}
                    href={href}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noopener" : undefined}
                    className="group flex min-h-[72px] items-center justify-between gap-3 rounded-[13px] border border-home-marigoldDeep/35 bg-home-marigold px-3.5 py-2.5 shadow-[0_4px_14px_rgba(23,62,47,0.04)] transition hover:-translate-y-0.5 hover:bg-home-marigoldDeep hover:shadow-[0_6px_18px_rgba(23,62,47,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-home-forest focus-visible:ring-offset-2"
                  >
                    <div className="min-w-0">
                      <h2 className="font-display text-[14px] font-bold leading-[1.15] text-home-ink md:text-[15px]">
                        {label}
                      </h2>
                      <p className="mt-1 font-ui text-[11px] leading-[1.25] text-home-forest/80">
                        {description}
                      </p>
                    </div>
                    <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 text-home-forest transition-transform group-hover:translate-x-0.5" />
                  </Link>
                ))}
              </nav>
            </div>
          </section>
        </main>

        {!webView && <HomeFooter />}
      </div>
    </>
  );
};

export default AboutUs;
